import React, { useEffect, useState } from 'react';
import { useUser } from '../context/userContext';
import { useRooms } from '../context/RoomsContext';
import { convertCelsiusToFahrenheit } from '../utils/helpers';
import { fetchApi } from '../utils/fetchApi';
import { useTranslation } from 'react-i18next';
import plusIcon from '../assets/icon-plus.svg';
import minusIcon from '../assets/icon-minus.svg';
import temperatureIcon from '../assets/temp.svg';
import humidityIcon from '../assets/humidity.svg';
import snowflakeIcon from '../assets/snow.svg';
import lockIcon from '../assets/lock.svg';
import lockIconOff from '../assets/lock_off.svg';
import gasIcon from '../assets/gas.svg';
import infoIcon from '../assets/icon-info.svg';
import infoIconOrange from '../assets/icon-info-orange.svg';
import { FaPencilAlt, FaCheck, FaSpinner } from 'react-icons/fa';

interface TemperatureCardProps {
  room?: string;
  thermostatId: number;
  temperature: number | string;
  humidity?: number | string;
  measuredTemperature: number | string;
  heatOrFreeze?: 'heat' | 'freeze';
  condensationRiskLevel?: number;
  childLock: boolean;
  onClick?: React.MouseEventHandler;
  dewPoint?: number;
}

const TemperatureCard: React.FC<TemperatureCardProps> = ({
  room,
  thermostatId,
  temperature,
  humidity,
  heatOrFreeze,
  measuredTemperature,
  condensationRiskLevel,
  childLock,
  dewPoint,
  onClick
}) => {
  const { unit } = useUser();
  const { rooms, setRooms } = useRooms();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedRoomName, setEditedRoomName] = useState(room || '');
  const [childLockStatus, setChildLockStatus] = useState(false);
  const [isChildLockLoading, setIsChildLockLoading] = useState(true);

  useEffect(() => {
    setChildLockStatus(childLock);
    setIsChildLockLoading(false);
  }, [childLock]);

  const handleChildLockToggle =  async () => {
    try {
    await fetchApi('update_thermostat_settings', {
      thermostat_id: thermostatId,
      child_lock: !childLockStatus,
    });
     
    setChildLockStatus(!childLockStatus);

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating room name:', error);
    }
  };

  const displayTemperature = unit === 'Fahrenheit' ? convertCelsiusToFahrenheit(temperature as number) : temperature;
  const displayMeasuredTemperature = unit === 'Fahrenheit' ? convertCelsiusToFahrenheit(measuredTemperature as number) : measuredTemperature;

  const unitSymbol = unit === 'Fahrenheit' ? '°F' : '°C';

  const handleSaveName = async () => {
    try {
      await fetchApi('update_thermostat_settings', {
        thermostat_id: thermostatId,
        new_name: editedRoomName,
      });

      setRooms(rooms.map((r) =>
        r.thermostatId === thermostatId ? { ...r, name: editedRoomName } : r
      ));

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating room name:', error);
    }
  };

  const handleEditName = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsEditing(true)
  }

  const handleTemperatureChange = async (delta: number) => {
    try {
      const newTemperature = (temperature as number) + delta;
      await fetchApi('update_thermostat_settings', {
        thermostat_id: thermostatId,
        temp_setpoint_degC: newTemperature,
      });

      setRooms(rooms.map((r) =>
        r.thermostatId === thermostatId ? { ...r, temperature: newTemperature } : r
      ));
    } catch (error) {
      console.error('Error updating temperature:', error);
    }
  };

  const getModeIcon = () => {
    if (heatOrFreeze === 'freeze') {
      return <img src={snowflakeIcon} alt="snowflake" />;
    } else if (heatOrFreeze === 'heat') {
      return <img src={gasIcon} alt="temperature" />;
    }
    return null;
  };

  const getModeText = () => {
    if (heatOrFreeze === 'freeze') {
      return t('cooling');
    } else if (heatOrFreeze === 'heat') {
      return t('heating');
    }
    return '';
  };

  return (
    <div className={
      `temperatureCard ${condensationRiskLevel === 2 ? "dewPointDisabled" : ""} 
      ${condensationRiskLevel === 0 ? "dewPointWarning" : ""}`}
    >
      {/* Top Section */}
      <div className="cardTop">
        <div className='controls'>
        <div className="roomInfo">
          <div className="roomName">
            {isEditing ? (
              <input
                type="text"
                value={editedRoomName}
                onChange={(e) => setEditedRoomName(e.target.value)}
                onBlur={handleSaveName}
                autoFocus
                className="roomInput"
              />
            ) : (
              <h3 
                onClick={(event) => {handleEditName(event)}} 
                className={`roomTitle ${editedRoomName.length > 9 ? 'longText' : ''}`}
              >
                {editedRoomName.length > 10 ? `${editedRoomName.substring(0, 9)}...` : editedRoomName}
              </h3>
            )}
              <button onClick={
                (event) => isEditing ? handleSaveName() : handleEditName(event)}
                className={`editButton ${isEditing ? "saveButton" : ""}`}
              >
              {isEditing ? <FaCheck /> : <FaPencilAlt />}
            </button>
          </div>
          <div className="modeInfo">
            {getModeIcon()}
            <span className="modeText">{getModeText()}</span>
          </div>
        </div>
        
        <div className="temperatureControls">
            {!isEditing && (<>
              <button 
                className="tempButton minus" 
                onClick={(e) => { e.stopPropagation(); handleTemperatureChange(-0.1); }}
              >
                <img src={minusIcon} alt="minus" />
              </button>
              <div className="targetTemperature">
                {displayTemperature &&
                  <span className="temperatureValue">
                    {Number(displayTemperature).toFixed(1)}{unitSymbol}
                  </span>
                }
              </div>
              <button 
                className="tempButton plus" 
                onClick={(e) => { e.stopPropagation(); handleTemperatureChange(0.1); }}
              >
                <img src={plusIcon} alt="plus" />
              </button>
            </>
              )}
        </div>
        </div>
        {condensationRiskLevel === 2 && (
          <div className='dewPointInfoTextBox coolingDisabledDewPoint'>
            <img src={infoIcon} alt="info" />
            <div className='dewPointInfoTextBoxContent'>
              <h5>{t('coolingDisabledDewPoint')}</h5>
              <p>{t('coolingDisabledDewPointDescription')}</p>
            </div>
          </div>
        )}
        {condensationRiskLevel === 1 && (
          <div className='dewPointInfoTextBox coolingDisabledWarning'>
            <img src={infoIconOrange} alt="info" />
            <div className='dewPointInfoTextBoxContent'>
              <h5>{t('coolingDisabledWarning')}</h5>
              <p>{t('coolingDisabledWarningDescription')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Section */}
      <div className="cardBottom">
        <div className="measurementPanel">
          <div className="measurementLabel">{t('measuredTemperature')}</div>
          <div className="measurementValue">
            <img src={temperatureIcon} alt="temperature" />
            <span>{displayMeasuredTemperature}{unitSymbol}</span>
          </div>
        </div>
        
        <div className="measurementPanel">
          <div className="measurementLabel">{t('humidity')}</div>
          <div className="measurementValue">
            <img src={humidityIcon} alt="humidity" />
            <span>{humidity}%</span>
          </div>
        </div>
        <div className='measurementPanel childLockPanel' title={t('childLock.lock')} onClick={handleChildLockToggle}>
          <div className="measurementLabel">{t('childLock')}</div>
          <div className="measurementValue">
            {isChildLockLoading ? <FaSpinner /> : (
              <>
                <span>{childLockStatus ? t('on').toUpperCase() : t('off').toUpperCase()}</span>
                <img src={childLockStatus ? lockIcon : lockIconOff} alt="lock" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemperatureCard;
